import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import SearchBar from "../../../../Components/Utils/SearchBar";
import { FilterButton, CancelButton, SubmitButton } from "../../../../Components/Utils/Buttons";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import ViewToggle from "../../../../Components/Utils/ViewToggle";
import MaterialVsAlternatePDFLink from "./Pdf/MaterialVsAlternatePDFLink";
import UseGetDataSearch from "../../../../Components/Hooks/useFetchDataBySearch";
import { useFormik } from "formik";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import useFetchMDIndex from "../../../../Components/Hooks/useFetchMDIndex";

// Realistic fallback initial data for design & development preview
const MOCK_DATA = [
  {
    id: 1,
    sno: 1,
    product_name: 'Nut CL 10 mm (High Tensile Grade 8.8)',
    product_code: 'NUT-CL-010',
    category: 'Fasteners & Hardware',
    product_unit: 'PCS',
    alternate_product: 'Nut CL 10 mm Stainless Steel (SS304)',
    alternate_notes: 'Corrosion resistant alternative with identical thread pitch',
    has_alternate: true,
  },
  {
    id: 2,
    sno: 2,
    product_name: '6" Plastic Vernier Caliper, Color: White',
    product_code: 'CAL-VNR-006',
    category: 'Measuring Instruments',
    product_unit: 'PCS',
    alternate_product: '6" Digital Vernier Caliper LCD Display (Stainless Steel)',
    alternate_notes: 'Upgraded digital readout version for precision tolerance',
    has_alternate: true,
  },
  {
    id: 3,
    sno: 3,
    product_name: '8" Linesman Plier, Stanley - 84-029',
    product_code: 'PLR-LMN-084',
    category: 'Hand Tools',
    product_unit: 'PCS',
    alternate_product: '8" Heavy-Duty Insulated Linesman Plier, Irwin Vise-Grip',
    alternate_notes: '1000V VDE certified equivalent for electrical works',
    has_alternate: true,
  },
  {
    id: 4,
    sno: 4,
    product_name: '6" Diagonal Plier, Stanley - 84-105',
    product_code: 'PLR-DGN-084',
    category: 'Hand Tools',
    product_unit: 'PCS',
    alternate_product: '',
    alternate_notes: '',
    has_alternate: false,
  },
  {
    id: 5,
    sno: 5,
    product_name: '9W LED Bulb 220-240V 720Lumen 80Lm/W 4000K',
    product_code: 'LGT-BULB-009',
    category: 'Electrical & Lighting',
    product_unit: 'PCS',
    alternate_product: '10W LED Bulb 850 Lumen 4000K Cool White (Philips)',
    alternate_notes: 'Higher luminous efficacy and extended lifetime rating',
    has_alternate: true,
  },
  {
    id: 6,
    sno: 6,
    product_name: 'Digital Multimeter, Sanwa - CD800A',
    product_code: 'ELE-DMM-800',
    category: 'Electrical & Testing',
    product_unit: 'PCS',
    alternate_product: 'Fluke 101 Basic Pocket Digital Multimeter',
    alternate_notes: 'Compact industrial standard alternative',
    has_alternate: true,
  },
  {
    id: 7,
    sno: 7,
    product_name: 'Grinder 7" Heavy Duty 2200W',
    product_code: 'PWR-GRD-007',
    category: 'Power Tools',
    product_unit: 'PCS',
    alternate_product: 'Bosch GWS 2200-180 Professional 7" Angle Grinder',
    alternate_notes: 'Identical 2200W power output with anti-vibration handle',
    has_alternate: true,
  },
  {
    id: 8,
    sno: 8,
    product_name: 'Industrial Hydraulic Hose 1/2" 2-Wire 5000 PSI',
    product_code: 'HYD-HSE-012',
    category: 'Hydraulics & Pneumatics',
    product_unit: 'MTR',
    alternate_product: '',
    alternate_notes: '',
    has_alternate: false,
  },
];

// Card View Wrapper
const MaterialVsAlternateCard = ({ item }) => {
  return (
    <div className="relative border border-gray-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-5 flex flex-col gap-4 overflow-hidden border-l-4 border-l-[#048178]">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-teal-50 text-[#048178] font-bold text-xs flex items-center justify-center border border-teal-200/60">
            {item.sno || "#"}
          </span>
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            {item.category || "General"}
          </span>
        </div>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">
          Unit: {item.product_unit || item.unit || "-"}
        </span>
      </div>

      <div className="bg-[#f8fafc] rounded-xl p-3.5 border border-slate-200/70 flex flex-col gap-3">
        {/* Material Requested */}
        <div>
          <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
            Material Requested
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <h4 className="font-bold text-sm text-[#111537] leading-snug">
              {item.product_name || item.product || "-"}
            </h4>
            {item.product_name && <CopyButton text={item.product_name} />}
          </div>
          {item.product_code && (
            <span className="text-[11px] text-slate-500 font-mono mt-0.5 block">
              Ref: {item.product_code}
            </span>
          )}
        </div>

        {/* Divider / Arrow */}
        <div className="flex items-center gap-2 my-0.5">
          <div className="flex-1 h-[1px] bg-slate-200"></div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100/80 text-[#048178]">
            vs Alternate
          </span>
          <div className="flex-1 h-[1px] bg-slate-200"></div>
        </div>

        {/* Alternate Product */}
        <div>
          <span className="text-[10px] font-bold tracking-wider text-teal-600 uppercase">
            Alternate Product
          </span>
          {item.alternate_product || item.alternateProduct ? (
            <div className="mt-0.5">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-xs text-[#048178]">
                  {item.alternate_product || item.alternateProduct}
                </p>
                <CopyButton text={item.alternate_product || item.alternateProduct} />
              </div>
              {item.alternate_notes && (
                <p className="text-[11px] text-slate-500 font-normal italic mt-1">
                  Note: {item.alternate_notes}
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic mt-0.5">
              No alternate material assigned
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

function MaterialRequestedVsAlternateIndex() {
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(
    localStorage.getItem("perPageView") || 20
  );
  const [search, setSearch] = useState("");
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filter, setFilter] = useState(false);

  const [viewMode, setViewMode] = useState(
    localStorage.getItem("materialVsAltViewMode") || "table"
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveViewMode = isMobile ? "card" : viewMode;

  const [filterProps, setFilterProps] = useState({
    category: "",
    unit: "",
    alternateStatus: "",
  });

  const [params, setParams] = useState({
    page: page,
    per_page: pageCount,
    search: "",
    category: "",
    unit: "",
    alternateStatus: "",
  });

  // Category & Unit dropdowns
  const { data: categoryOptions } = UseDropDown("/category/drop-down");
  const { value: unitOptions } = useFetchMDIndex(
    `/dropdown/dropdown-details/`,
    "Unit_item",
    showFilterDialog
  );

  // Hook for API (will return data once API is active; fallback to MOCK_DATA when empty)
  const {
    data: apiData,
    totalCount: apiTotal,
    dataLoading,
    getData,
  } = UseGetDataSearch("/procurement/material-requested-vs-alternate", params);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: filterProps,
    onSubmit: (values) => {
      setParams((prev) => ({
        ...prev,
        category: values.category,
        unit: values.unit,
        alternateStatus: values.alternateStatus,
        page: 1,
      }));
      setPage(1);
      setFilter(Boolean(values.category || values.unit || values.alternateStatus));
      setShowFilterDialog(false);
    },
  });

  const clearFilter = () => {
    formik.resetForm();
    setFilterProps({ category: "", unit: "", alternateStatus: "" });
    setParams((prev) => ({
      ...prev,
      category: "",
      unit: "",
      alternateStatus: "",
      page: 1,
    }));
    setFilter(false);
  };

  // Filter and prepare displayed data
  const rawList = apiData && apiData.length > 0 ? apiData : MOCK_DATA;

  const filteredData = useMemo(() => {
    return rawList.filter((item) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        (item.product_name || item.product || "").toLowerCase().includes(q) ||
        (item.product_code || "").toLowerCase().includes(q) ||
        (item.category || item.category_name || "").toLowerCase().includes(q) ||
        (item.alternate_product || item.alternateProduct || "").toLowerCase().includes(q);

      const matchCat =
        !params.category ||
        (item.category || item.category_name || "")
          .toLowerCase()
          .includes(params.category.toLowerCase());

      const matchUnit =
        !params.unit ||
        (item.product_unit || item.unit || "")
          .toLowerCase()
          .includes(params.unit.toLowerCase());

      const matchAlt =
        !params.alternateStatus ||
        (params.alternateStatus === "has" && Boolean(item.alternate_product || item.alternateProduct)) ||
        (params.alternateStatus === "none" && !item.alternate_product && !item.alternateProduct);

      return matchSearch && matchCat && matchUnit && matchAlt;
    });
  }, [rawList, search, params]);

  const totalCount = apiTotal || filteredData.length;

  return (
    <div className="w-full h-full px-4 py-2">
      {/* Top Breadcrumb & Controls */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Procurement", link: "/procurement" },
              { name: "Material Requested vs Alternate" },
            ]}
          />
          {/* <div className="flex md:hidden items-center gap-1.5 flex-wrap">
            <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && <FilterButton handleClick={clearFilter} label="Clear" />}
            <MaterialVsAlternatePDFLink data={filteredData} />
          </div> */}
        </div>

        <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center justify-between md:justify-start gap-2">
          {/* <div className="w-full md:w-auto flex-1 min-w-0">
            <SearchBar
              handleChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              value={search}
              placeholder="Search product, category, alternate..."
            />
          </div> */}

          <div className="hidden md:flex items-center gap-2">
            <ViewToggle
              viewMode={effectiveViewMode}
              onViewChange={(mode) => {
                setViewMode(mode);
                localStorage.setItem("materialVsAltViewMode", mode);
              }}
              storageKey="materialVsAltViewMode"
            />
            {/* <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && <FilterButton handleClick={clearFilter} label="Clear" />} */}
            <MaterialVsAlternatePDFLink data={filteredData} />
          </div>
        </div>
      </div>

      {/* Main Table / Card View */}
      <PremiumTable
        data={filteredData}
        total={totalCount}
        pageIndex={page}
        setPageIndex={setPage}
        dataCount={pageCount}
        setDataCount={setPageCount}
        viewMode={effectiveViewMode}
        headers={[
          { title: "S.No", width: "80px", align: "center" },
          { title: "Product (Material Requested)", width: "35%" },
          { title: "Category", width: "18%" },
          { title: "Unit", width: "100px", align: "center" },
          { title: "Alternate Product", width: "32%" },
        ]}
        renderRow={(item, idx) => {
          const hasAlt = Boolean(item.alternate_product || item.alternateProduct);
          return (
            <tr
              key={item.id || idx}
              className="bg-white hover:bg-teal-50/30 transition-colors align-middle border-b border-gray-100"
            >
              {/* S.No */}
              <td className="p-4 text-center align-middle border-t border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl">
                <span className="w-7 h-7 mx-auto rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center">
                  {item.sno || idx + 1}
                </span>
              </td>

              {/* Material Requested */}
              <td className="p-4 align-middle border-t border-gray-200/60">
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm text-[#111537]">
                      {item.product_name || item.product || "-"}
                    </span>
                    {item.product_name && <CopyButton text={item.product_name} />}
                  </div>
                  {item.product_code && (
                    <span className="text-[11px] text-slate-400 font-mono mt-0.5">
                      Code: {item.product_code}
                    </span>
                  )}
                </div>
              </td>

              {/* Category */}
              <td className="p-4 align-middle border-t border-gray-200/60">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200/70">
                  {item.category_name || item.category || "-"}
                </span>
              </td>

              {/* Unit */}
              <td className="p-4 text-center align-middle border-t border-gray-200/60">
                <span className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700">
                  {item.product_unit || item.unit || "-"}
                </span>
              </td>

              {/* Alternate Product */}
              <td className="p-4 align-middle border-t border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
                {hasAlt ? (
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-sm text-[#048178]">
                        {item.alternate_product || item.alternateProduct}
                      </span>
                      <CopyButton text={item.alternate_product || item.alternateProduct} />
                    </div>
                    {item.alternate_notes && (
                      <span className="text-[11px] text-slate-500 font-normal italic mt-0.5">
                        {item.alternate_notes}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic font-light">
                    No alternate product
                  </span>
                )}
              </td>
            </tr>
          );
        }}
        renderCard={(item, idx) => (
          <MaterialVsAlternateCard key={item.id || idx} item={item} />
        )}
        dataLoading={dataLoading}
      />

      {/* Filter Modal Dialog */}
      {showFilterDialog && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center p-3 sm:p-4">
          <div className="animate-scale-in w-full sm:w-[480px] max-w-[95vw] max-h-[90vh] bg-white py-4 px-4 sm:px-6 rounded-xl shadow-lg overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-lg font-bold text-gray-800">
                Filter Material Requested vs Alternate
              </h2>
              <button
                type="button"
                onClick={() => setShowFilterDialog(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                &times;
              </button>
            </div>

            <form onSubmit={formik.handleSubmit}>
              <div className="flex flex-col gap-3 mb-4">
                <FormikSelectTwo
                  label="Category"
                  placeholder="Select Category"
                  formik={formik}
                  Options={categoryOptions || []}
                  name="category"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Unit"
                  placeholder="Select Unit"
                  formik={formik}
                  Options={unitOptions || []}
                  name="unit"
                  width="w-full"
                />

                <FormikSelectTwo
                  label="Alternate Availability"
                  placeholder="Choose"
                  formik={formik}
                  Options={[
                    { label: "All Items", value: "" },
                    { label: "Has Alternate Product", value: "has" },
                    { label: "No Alternate Product", value: "none" },
                  ]}
                  name="alternateStatus"
                  width="w-full"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                <CancelButton handleClick={() => setShowFilterDialog(false)} />
                <SubmitButton name="Apply" />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialRequestedVsAlternateIndex;
